export const EFFECT_MANIFEST = Object.freeze({
  id: 'tone.tempoPitch',
  label: 'Tempo & Pitch',
  version: '1.4.0',
  inputParam: 'playbackRate',
  dimensionId: 'EW::I',
  dimensionLabel: 'EW::I',
  defaults: Object.freeze({
    playbackRate: 1,
  }),
  userParamSpec: Object.freeze({
    id: 'playbackRate',
    label: 'Tempo Control',
    units: '%',
    range: Object.freeze({ min: -100, max: 100, equilibrium: 0 }),
  }),
  modules: Object.freeze([
    Object.freeze({
      id: 'tempoFine',
      engineRequirement: 'stream-safe',
      label: 'Tempo Fine (±16%)',
      description: 'Continuous tempo modulation around neutral speed (no reverse).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({ min: -16, max: 16, equilibrium: 0, units: '%' }),
      initialRange: Object.freeze({ min: -16, max: 16, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.12,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0.84, max: 1.16, transform: 'linear' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    Object.freeze({
      id: 'tempoWide',
      engineRequirement: 'stream-safe',
      label: 'Tempo Wide (±100%)',
      description: 'Expressive tempo sweep from -100% to +100% (forward only).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({ min: -100, max: 100, equilibrium: 0, units: '%' }),
      initialRange: Object.freeze({ min: -100, max: 100, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.15,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0, max: 2, transform: 'linear' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    Object.freeze({
      id: 'pitchFine',
      engineRequirement: 'stream-safe',
      label: 'Pitch Fine (±12st)',
      description: 'Quantized semitone shifts within ±12 semitones (forward only).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({
        min: -12,
        max: 12,
        equilibrium: 0,
        units: 'st',
        quantize: Object.freeze({ step: 1, unit: 'st' }),
      }),
      initialRange: Object.freeze({ min: -12, max: 12, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.12,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0.25, max: 4, transform: 'exponential' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    Object.freeze({
      id: 'pitchWide',
      engineRequirement: 'stream-safe',
      label: 'Pitch Wide (±24st)',
      description: 'Extended chromatic semitone shifts within ±24 semitones (forward only).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({
        min: -24,
        max: 24,
        equilibrium: 0,
        units: 'st',
        quantize: Object.freeze({ step: 1, unit: 'st' }),
      }),
      initialRange: Object.freeze({ min: -24, max: 24, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.15,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0.1, max: 8, transform: 'exponential' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    // ——— Time-stretch engine modules ———
    // The classic modules above are TAPE: pitch follows speed (varispeed).
    // These modules decouple the two axes on the stretch engine: tempo moves
    // with pitch locked, or pitch moves with tempo locked. They require the
    // stretch engine (which implies the prebuffer backend); without it they
    // degrade to the tape behavior of their classic counterparts.
    Object.freeze({
      id: 'tempoStretchFine',
      engineRequirement: 'stretch-required',
      rateMode: 'stretch',
      label: 'Stretch Fine (±16%)',
      description: 'Continuous tempo modulation with pitch locked (time-stretch).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({ min: -16, max: 16, equilibrium: 0, units: '%' }),
      initialRange: Object.freeze({ min: -16, max: 16, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.12,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0.84, max: 1.16, transform: 'linear' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    Object.freeze({
      id: 'tempoStretchWide',
      engineRequirement: 'stretch-required',
      rateMode: 'stretch',
      label: 'Stretch Wide (±100%)',
      description: 'Expressive tempo sweep with pitch locked (time-stretch).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({ min: -100, max: 100, equilibrium: 0, units: '%' }),
      initialRange: Object.freeze({ min: -100, max: 100, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.15,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0, max: 2, transform: 'linear' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    Object.freeze({
      id: 'pitchPureFine',
      engineRequirement: 'stretch-required',
      pitchMode: 'engine',
      label: 'Pure Pitch (±12st)',
      description: 'Quantized semitone shifts with tempo locked (no speed change).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({
        min: -12,
        max: 12,
        equilibrium: 0,
        units: 'st',
        quantize: Object.freeze({ step: 1, unit: 'st' }),
      }),
      initialRange: Object.freeze({ min: -12, max: 12, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.12,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0.25, max: 4, transform: 'exponential' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    Object.freeze({
      id: 'pitchPureWide',
      engineRequirement: 'stretch-required',
      pitchMode: 'engine',
      label: 'Pure Pitch (±24st)',
      description: 'Extended chromatic semitone shifts with tempo locked (no speed change).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({
        min: -24,
        max: 24,
        equilibrium: 0,
        units: 'st',
        quantize: Object.freeze({ step: 1, unit: 'st' }),
      }),
      initialRange: Object.freeze({ min: -24, max: 24, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.15,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0.1, max: 8, transform: 'exponential' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    // ——— Speed & Reverse (bidirectional) ———
    // One continuous knob for speed AND direction, resting at normal play. The
    // input maps linearly onto a signed rate of −1..+3: centre = 1× forward,
    // one way speeds up to 3×, the other slows through a real stop (the sink
    // glides to silence at 0) and on into 1× reverse. The engine reads its one
    // stored buffer backwards past the crossing (no flipped copy), so it is
    // seamless. Two flavors, like the tempo pair: tape (pitch bends with speed)
    // and stretch (pitch locked).
    //
    // "Normal" means the deck's TRANSPORT tempo, so on a warped deck centre sits
    // on the shared grid and the knob's magnitude drives the tempo like the
    // tempo faders do (the sign stays a read direction — there is no negative
    // tempo). It used to mean the track's NATIVE tempo on every deck, which put
    // a warped deck off its own grid the moment the knob was touched.
    Object.freeze({
      id: 'speedReverseTape',
      engineRequirement: 'stretch-required',
      speedMode: 'speedReverse',
      label: 'Speed & Reverse (tape)',
      description: 'Normal at centre; speeds up one way, slows through a stop into reverse the other — pitch bends with speed (tape).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({ min: -100, max: 100, equilibrium: 0, units: '%' }),
      initialRange: Object.freeze({ min: -100, max: 100, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.1,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: -1, max: 3, transform: 'linear' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
    Object.freeze({
      id: 'speedReverseStretch',
      engineRequirement: 'stretch-required',
      speedMode: 'speedReverse',
      rateMode: 'stretch',
      label: 'Speed & Reverse (stretch)',
      description: 'Normal at centre; speeds up one way, slows through a stop into reverse the other — pitch stays locked (time-stretch).',
      dimension: 'EW::I',
      target: 'playbackRate',
      valueRange: Object.freeze({ min: -100, max: 100, equilibrium: 0, units: '%' }),
      initialRange: Object.freeze({ min: -100, max: 100, equilibrium: 0 }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'playbackRate',
        provider: 'playbackController',
        smoothing: Object.freeze({
          defaultRamp: 0.1,
          minRamp: 0.02,
          maxRamp: 2,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: -1, max: 3, transform: 'linear' }),
        secondaryParameters: Object.freeze([]),
      }),
    }),
  ]),
});

export default EFFECT_MANIFEST;
